import React from 'react';
import { PageModule } from '../types';
import RichTextEditor from './RichTextEditor';
import { unescapeHtml } from '../utils/unescapeHtml';

interface ContentModuleEditorProps {
    modules: PageModule[];
    onChange: (modules: PageModule[]) => void;
    onImageRequest: (moduleId: string) => void;
}

const ContentModuleEditor: React.FC<ContentModuleEditorProps> = ({ modules, onChange, onImageRequest }) => {

    const updateModule = (id: string, updates: Partial<PageModule>) => {
        const newModules = modules.map(m => m.id === id ? { ...m, ...updates } : m);
        onChange(newModules);
    };

    const deleteModule = (id: string) => {
        if (!confirm('Remove this section?')) return;
        onChange(modules.filter(m => m.id !== id));
    };

    const moveModule = (id: string, dir: 'up' | 'down') => {
        const idx = modules.findIndex(m => m.id === id);
        if (idx === -1) return;
        const newModules = [...modules];
        if (dir === 'up' && idx > 0) {
            [newModules[idx], newModules[idx - 1]] = [newModules[idx - 1], newModules[idx]];
        } else if (dir === 'down' && idx < newModules.length - 1) {
            [newModules[idx], newModules[idx + 1]] = [newModules[idx + 1], newModules[idx]];
        }
        onChange(newModules);
    };

    const addModule = (type: 'text' | 'text-image') => {
        const newMod: PageModule = {
            id: crypto.randomUUID(),
            type,
            title: '',
            text: '',
            order: modules.length,
            imagePosition: 'right' // Default
        };
        onChange([...modules, newMod]);
    };

    return (
        <div className="space-y-4">
            <div className="space-y-6">
                {modules.map((m, idx) => (
                    <div key={m.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-gray-300 transition-all shadow-sm space-y-6 relative group">
                        {/* Module Controls */}
                        <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button onClick={() => moveModule(m.id, 'up')} className="bg-gray-100 p-2 rounded-full hover:bg-black hover:text-white transition-colors" title="Move Up">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
                            </button>
                            <button onClick={() => moveModule(m.id, 'down')} className="bg-gray-100 p-2 rounded-full hover:bg-black hover:text-white transition-colors" title="Move Down">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                            </button>
                            <button onClick={() => deleteModule(m.id)} className="bg-vanity-pink/10 text-vanity-pink p-1.5 rounded-full hover:bg-vanity-pink hover:text-white transition-colors" title="Delete">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
                            </button>
                        </div>

                        <div className={`grid gap-8 ${m.type === 'text-image' ? 'grid-cols-1 md:grid-cols-12' : 'grid-cols-1'}`}>
                            {/* Text Part */}
                            {/* Logic for image pos: user might want left or right. Standardizing on flex order or cols. 
                                Using boolean check for layout.
                            */}
                            <div className={m.type === 'text-image' ? (m.imagePosition === 'left' ? 'md:col-span-8 md:order-2' : 'md:col-span-8 md:order-1') : ''}>
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Section Heading</label>
                                            {m.type === 'text-image' && (
                                                <button
                                                    onClick={() => updateModule(m.id, { imagePosition: m.imagePosition === 'left' ? 'right' : 'left' })}
                                                    className="text-[9px] uppercase font-bold text-gray-300 hover:text-black"
                                                >
                                                    Img: {m.imagePosition}
                                                </button>
                                            )}
                                        </div>
                                        <input type="text" placeholder="Title for this block..." value={m.title || ''} onChange={e => updateModule(m.id, { title: e.target.value })} className="w-full text-xl font-serif italic border-0 border-b border-gray-100 pb-1.5 focus:border-vanity-pink outline-none" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Narrative Text</label>
                                        <RichTextEditor
                                            name={`module-text-${m.id}`}
                                            value={unescapeHtml(m.text || '')}
                                            onChange={val => updateModule(m.id, { text: val })}
                                            height="200px"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Image Part */}
                            {m.type === 'text-image' && (
                                <div className={m.imagePosition === 'left' ? 'md:col-span-4 md:order-1' : 'md:col-span-4 md:order-2'}>
                                    <div onClick={() => onImageRequest(m.id)} className="aspect-square bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center overflow-hidden hover:border-vanity-pink transition-all relative group cursor-pointer shadow-inner">
                                        {m.imageUrl ? <img src={m.imageUrl} className="w-full h-full object-cover" /> : <span className="font-bold text-gray-300">Select Image</span>}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                            <span className="text-white font-black uppercase text-[10px] tracking-widest text-center px-4 leading-relaxed">Change Asset</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {modules.length === 0 && (
                    <div className="py-16 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        <p className="font-serif italic text-xl text-gray-300">No content modules yet. Start building your narrative.</p>
                    </div>
                )}
            </div>

            {/* Add Buttons */}
            <div className="grid grid-cols-2 gap-4">
                <button onClick={() => addModule('text')} className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200 hover:border-black hover:bg-gray-100 transition-all text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-black">
                    + Text Block
                </button>
                <button onClick={() => addModule('text-image')} className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200 hover:border-black hover:bg-gray-100 transition-all text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-black">
                    + Text & Image Block
                </button>
            </div>
        </div>
    );
};

export default ContentModuleEditor;
